"use client";

import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { signIn } from "@/lib/auth/client";
import { rememberAuthMethod } from "@/lib/auth/last-method";
import { LastUsedPill } from "./last-used-pill";

type Props = {
	/** Where Better Auth should redirect after a successful signin. */
	callbackURL: string;
	/** Localized "Continue with Google" label. */
	label: string;
	/** Localized fallback label shown when Google fails. */
	magicLinkFallbackLabel?: string;
	/** Show the "Last used" badge — this was the visitor's previous method. */
	lastUsed?: boolean;
	/** Localized "Last used" badge label. */
	lastUsedLabel?: string;
	/**
	 * Which surface renders this button — gates whether a press fires
	 * `signup_started` (new-account funnel), `login_started` (returning
	 * visitor), or neither. Before this, OAuth signups never fired
	 * `signup_started` at all (it only fired from the magic-link form), so
	 * the funnel undercounted starts and completion-rate math ran on a base
	 * that silently excluded Google/LinkedIn entirely.
	 *
	 * `"connect"` is for an already-authenticated user linking an additional
	 * OAuth provider from account settings — NOT a signup or login funnel
	 * step. Firing either funnel event from there would pollute the funnel
	 * with "connect a provider from settings" presses, so this context is a
	 * no-op for `signup_started`/`login_started` (the raw
	 * `google_signin_clicked` click telemetry below still fires unaffected).
	 */
	context: "signup" | "login" | "connect";
	/** Called first on click, before the OAuth redirect starts (attribution). */
	onPress?: () => void;
};

/**
 * One-click Google signin button. Triggers Better Auth's `signIn.social`
 * with the `google` provider; Better Auth handles the OIDC redirect and
 * post-callback session creation. Hidden by the parent when the provider
 * isn't configured server-side, so this component never decides whether
 * the integration is live.
 *
 * Emits `google_signin_clicked` (always, on press — raw click telemetry) and
 * `google_signin_failed` (only if all attempts fail — success lands on
 * a fresh page so we can't fire a `_succeeded` event from here; the
 * server-side `oauth_account_linked` event covers that side). Also fires
 * `signup_started` (`context="signup"`) or `login_started`
 * (`context="login"`) so OAuth starts count toward the same funnel as the
 * magic-link path instead of being invisible.
 *
 * Transient "Failed to fetch" / "Load failed" errors (the OIDC discovery /
 * `/sign-in/social` network blip that surfaced as 0 completions right after
 * launch — 3/4 clicks failed while LinkedIn, which already retried, did not)
 * are retried up to MAX_RETRIES times with exponential backoff before
 * surfacing. Mirrors `LinkedInSignInButton`; keep the two in sync.
 */
// Safari/WebKit surfaces network errors as "Load failed" rather than the
// standard "Failed to fetch" — treat both as retryable.
const TRANSIENT_MESSAGES = new Set(["Failed to fetch", "Load failed"]);
const MAX_RETRIES = 2;
const RETRY_DELAYS_MS = [500, 1500] as const;

export function GoogleSignInButton({
	callbackURL,
	label,
	magicLinkFallbackLabel,
	lastUsed,
	lastUsedLabel,
	context,
	onPress,
}: Props) {
	const posthog = usePostHog();
	const [error, setError] = useState<string | null>(null);
	const [pending, setPending] = useState(false);

	const onClick = async () => {
		onPress?.();
		setError(null);
		setPending(true);
		posthog?.capture("google_signin_clicked", {
			callback_url: callbackURL,
		});
		// Funnel instrumentation: mirror the magic-link form's `signup_started`
		// / `login_started` capture so OAuth starts aren't invisible to the
		// same funnel. Mutually exclusive with `MagicLinkForm`'s own capture —
		// a visitor takes one path or the other per session, never both.
		// `"connect"` (linking a provider from account settings) intentionally
		// fires neither — see the `context` prop doc above.
		if (context === "signup") {
			posthog?.capture("signup_started", {
				signup_method: "google",
				callback_url: callbackURL,
			});
		} else if (context === "login") {
			posthog?.capture("login_started", {
				method: "google",
			});
		}
		// Remember this method for the "Last used" hint on the next visit.
		rememberAuthMethod("google");

		let lastErr: unknown;
		let retryCount = 0;

		for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
			if (attempt > 0) {
				// Exponential backoff between retries — invisible to the user since
				// the button stays in its pending state throughout.
				await new Promise<void>((resolve) =>
					setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]),
				);
				retryCount = attempt;
			}
			try {
				await signIn.social({ provider: "google", callbackURL });
				// Redirect initiated — browser navigates away, component unmounts.
				return;
			} catch (err) {
				lastErr = err;
				const message = err instanceof Error ? err.message : "";
				if (!TRANSIENT_MESSAGES.has(message)) break; // non-retryable
			}
		}

		const message = lastErr instanceof Error ? lastErr.message : "unknown";
		const name = lastErr instanceof Error ? lastErr.name : "unknown";
		const isFetchError = TRANSIENT_MESSAGES.has(message);

		posthog?.capture("google_signin_failed", {
			callback_url: callbackURL,
			error_message: message,
			error_name: name,
			is_fetch_error: isFetchError,
			retry_count: retryCount,
		});
		setError(
			isFetchError
				? (magicLinkFallbackLabel ??
						"Connection error — try signing in with email below.")
				: "Google sign-in failed. Please try again.",
		);
		setPending(false);
	};

	return (
		<div className="flex flex-col gap-2">
			<button
				type="button"
				onClick={onClick}
				disabled={pending}
				className="relative flex w-full items-center justify-center gap-2 rounded-full border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent disabled:opacity-50"
			>
				<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
					<title>Google</title>
					<path
						d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
						fill="#4285F4"
					/>
					<path
						d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
						fill="#34A853"
					/>
					<path
						d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
						fill="#FBBC05"
					/>
					<path
						d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
						fill="#EA4335"
					/>
				</svg>
				{pending ? "…" : label}
				{lastUsed && lastUsedLabel ? (
					<LastUsedPill label={lastUsedLabel} />
				) : null}
			</button>
			{error ? (
				<p className="text-xs text-destructive" aria-live="polite">
					{error}
				</p>
			) : null}
		</div>
	);
}
